import { useLingui } from "@lingui/react/macro";
import { Button, cn } from "@rakazo/ui-web";
import { Check, ChevronUp, LoaderCircle, Lock, Mic, Trash2 } from "lucide-react";
import type { PointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { KeptDictation } from "../../lib/dictation";
import { dictation } from "../../lib/dictation";
import { IconTooltip } from "../IconTooltip";

/** How far the finger travels up before the recording locks hands-free. */
export const LOCK_DISTANCE_PX = 64;
/** A press shorter than this is a tap: nothing was said, so it only shows the hint. */
export const TAP_MS = 350;
const HINT_MS = 2_500;

type Recording = { locked: boolean; startedAt: number } | null;

/**
 * Telegram-style dictation: hold to talk and release to insert the text into the
 * composer; slide up while holding to lock, then insert or discard by hand. The
 * words stay editable and Send is a separate decision. Keyboard activation starts
 * locked, since a key cannot be held and slid.
 */
export function useHoldToTalk({
  transcribe,
  disabled,
  onSetup,
  onText,
}: {
  transcribe?: boolean;
  disabled?: boolean;
  /** Called instead of recording when no transcription provider is connected. */
  onSetup?: () => void;
  onText: (text: string) => void;
}) {
  const { t } = useLingui();
  const [recording, setRecording] = useState<Recording>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lift, setLift] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [kept, setKept] = useState<KeptDictation>(() => dictation.keptDictation);
  const recordingRef = useRef(recording);
  const onTextRef = useRef(onText);
  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);
  /** Dictation reported listening for this recording, so a later idle means it ended. */
  const started = useRef(false);
  /** Saved pieces before this recording, to tell whether this one added any. */
  const keptBefore = useRef(0);
  const gesture = useRef<{ startY: number; startedAt: number } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotice = useCallback((text: string) => {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), HINT_MS);
  }, []);

  useEffect(
    () =>
      dictation.subscribe((snapshot) => {
        if (!recordingRef.current) return;
        if (snapshot.status === "listening") started.current = true;
        setTranscript(snapshot.transcript);
        setTranscribing(snapshot.status === "transcribing");
        if (snapshot.status !== "idle" || !started.current) return;
        started.current = false;
        recordingRef.current = null;
        setRecording(null);
        setTranscribing(false);
        setLift(0);
        // Audio this recording saved has its own row with a retry; don't say it twice.
        if (snapshot.error && dictation.keptDictation.segments <= keptBefore.current) {
          const microphoneBlocked = /microphone access is blocked|not-allowed|permission/i.test(
            snapshot.error,
          );
          showNotice(
            microphoneBlocked
              ? t`Microphone access is blocked. Allow it in your system or browser settings.`
              : snapshot.error,
          );
        }
      }),
    [showNotice],
  );

  useEffect(() => dictation.subscribeKept(setKept), []);

  useEffect(
    () => () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      // Leaving the composer is not discarding: keep what was said for a retry.
      if (recordingRef.current) dictation.stop("keep");
    },
    [],
  );

  const retryKept = useCallback(() => {
    void dictation.retryKept((text) => onTextRef.current(text));
  }, []);
  const discardKept = useCallback(() => dictation.discardKept(), []);

  const start = useCallback(
    (locked: boolean) => {
      started.current = false;
      keptBefore.current = dictation.keptDictation.segments;
      setNotice(null);
      setTranscript("");
      const next = { locked, startedAt: Date.now() };
      recordingRef.current = next;
      setRecording(next);
      void dictation.listen({
        mode: "hold",
        transcribe,
        onFinal: (text) => onTextRef.current(text),
      });
    },
    [transcribe],
  );

  const submit = useCallback(() => {
    if (!recordingRef.current || transcribing) return;
    setTranscribing(true);
    dictation.submitHold();
  }, [transcribing]);

  const cancel = useCallback(() => {
    gesture.current = null;
    started.current = false;
    recordingRef.current = null;
    setRecording(null);
    setTranscribing(false);
    setLift(0);
    dictation.stop("cancel");
  }, []);

  const lock = useCallback(() => {
    gesture.current = null;
    setLift(0);
    const current = recordingRef.current;
    if (!current) return;
    const next = { ...current, locked: true };
    recordingRef.current = next;
    setRecording(next);
  }, []);

  const startLocked = () => {
    if (disabled || recordingRef.current) return;
    if (transcribe === false) {
      onSetup?.();
      return;
    }
    start(true);
  };

  return {
    recording,
    startLocked,
    transcribing,
    transcript,
    lift,
    notice,
    kept,
    retryKept,
    discardKept,
    submit,
    cancel,
    buttonProps: {
      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
        if (disabled || event.button !== 0 || recordingRef.current) return;
        event.preventDefault();
        if (transcribe === false) {
          onSetup?.();
          return;
        }
        event.currentTarget.setPointerCapture?.(event.pointerId);
        gesture.current = { startY: event.clientY, startedAt: Date.now() };
        start(false);
      },
      onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
        const hold = gesture.current;
        if (!hold || !recordingRef.current || recordingRef.current.locked) return;
        const travel = Math.max(0, hold.startY - event.clientY);
        if (travel >= LOCK_DISTANCE_PX) {
          lock();
          return;
        }
        setLift(travel);
      },
      onPointerUp: () => {
        const hold = gesture.current;
        gesture.current = null;
        setLift(0);
        if (!hold || !recordingRef.current || recordingRef.current.locked) return;
        if (Date.now() - hold.startedAt < TAP_MS) {
          // A tap starts a hands-free recording instead of cancelling: the person can
          // speak and then insert with the check button, without holding the phone.
          lock();
          return;
        }
        submit();
      },
      onPointerCancel: () => {
        if (!gesture.current) return;
        gesture.current = null;
        if (!recordingRef.current?.locked) cancel();
      },
      onClick: (event: { detail: number }) => {
        // detail 0 is a keyboard press; pointer presses are handled above.
        if (event.detail === 0) startLocked();
      },
      onContextMenu: (event: { preventDefault: () => void }) => event.preventDefault(),
    },
  };
}

/**
 * Speech that was recorded but not transcribed, from this dictation or an earlier visit.
 * It sits above the composer because on a phone the microphone button hides once there is text.
 */
export function HoldToTalkRecovery({ talk }: { talk: ReturnType<typeof useHoldToTalk> }) {
  const { t } = useLingui();
  const { kept } = talk;
  if (!kept.segments || talk.recording) return null;
  return (
    <div
      role="status"
      data-testid="dictation-recovery"
      className="mb-3 flex items-center gap-1 rounded-[14px] border border-border bg-muted ps-4 pe-1 text-sm text-foreground"
    >
      <span className="min-w-0 flex-1 py-2" dir="auto">
        {kept.retrying ? t`Transcribing…` : (kept.error ?? t`Dictation not transcribed`)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={kept.retrying}
        onClick={talk.retryKept}
        className="min-h-11 shrink-0"
      >
        {kept.retrying ? (
          <LoaderCircle aria-hidden="true" size={14} className="motion-safe:animate-spin" />
        ) : null}
        {t`Retry`}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t`Discard recording`}
        onClick={talk.discardKept}
        className="size-11 shrink-0 text-muted-foreground hover:text-foreground"
      >
        <Trash2 size={16} strokeWidth={1.8} />
      </Button>
    </div>
  );
}

function useSeconds(since: number | undefined, paused: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === undefined) return;
    setNow(Date.now());
    if (paused) return;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [since, paused]);
  return since === undefined ? 0 : Math.max(0, Math.floor((now - since) / 1000));
}

/** Takes the text field's place while recording: a live dot, the time, and what was heard. */
export function HoldToTalkStrip({ talk }: { talk: ReturnType<typeof useHoldToTalk> }) {
  const { t } = useLingui();
  const seconds = useSeconds(talk.recording?.startedAt, talk.transcribing);
  if (!talk.recording) return null;
  const hint = talk.transcribing
    ? t`Transcribing…`
    : talk.recording.locked
      ? t`Listening…`
      : t`Slide up to lock`;
  return (
    <div
      data-testid="hold-to-talk-strip"
      className="flex min-h-6 min-w-0 flex-1 items-center gap-2.5 py-0.5 text-sm"
    >
      {talk.transcribing ? (
        <LoaderCircle aria-hidden="true" size={16} className="shrink-0 motion-safe:animate-spin" />
      ) : (
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-destructive motion-safe:animate-pulse"
        />
      )}
      <span className="shrink-0 tabular-nums text-foreground/80">
        {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
      </span>
      <span
        className={cn("truncate", talk.transcript ? "text-foreground" : "text-muted-foreground")}
        dir="auto"
        aria-live="polite"
      >
        {talk.transcribing ? hint : talk.transcript || hint}
      </span>
    </div>
  );
}

/** The microphone button, plus the lock target above it and Cancel/Send once locked. */
export function HoldToTalkControls({
  talk,
  disabled,
}: {
  talk: ReturnType<typeof useHoldToTalk>;
  disabled?: boolean;
}) {
  const { t } = useLingui();
  const { recording } = talk;
  if (talk.transcribing) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Discard recording`}
          onClick={talk.cancel}
          className="size-11 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
        >
          <Trash2 size={16} strokeWidth={1.8} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          aria-label={t`Transcribing…`}
          aria-busy="true"
          disabled
          className="size-11 shrink-0 rounded-full"
        >
          <LoaderCircle aria-hidden="true" size={18} className="motion-safe:animate-spin" />
        </Button>
      </>
    );
  }
  if (recording?.locked) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Discard recording`}
          onClick={talk.cancel}
          className="size-11 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
        >
          <Trash2 size={16} strokeWidth={1.8} />
        </Button>
        <Button
          size="icon"
          aria-label={t`Insert dictation`}
          disabled={talk.transcribing}
          onClick={talk.submit}
          className="size-11 shrink-0 rounded-full shadow-sm"
        >
          <Check size={16} strokeWidth={2.2} />
        </Button>
      </>
    );
  }
  return (
    <span className="relative isolate inline-flex">
      {recording ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-2 scale-140 rounded-full bg-destructive/15 ring-4 ring-destructive/10 motion-safe:animate-pulse"
        />
      ) : null}
      {recording ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-full start-1/2 mb-3 flex flex-col items-center gap-0.5 rounded-full border border-border bg-card px-2 py-2 text-muted-foreground shadow-md transition-transform motion-reduce:transition-none"
          style={{ transform: `translate(-50%, ${-talk.lift}px)` }}
        >
          <Lock size={14} strokeWidth={1.8} />
          <ChevronUp size={14} strokeWidth={1.8} className="motion-safe:animate-bounce" />
        </span>
      ) : null}
      {talk.notice && !recording ? (
        <span
          role="status"
          className="pointer-events-none absolute bottom-full end-0 mb-2 w-max max-w-64 rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground shadow-md"
        >
          {talk.notice}
        </span>
      ) : null}
      <IconTooltip label={t`Dictate`}>
        <Button
          variant="outline"
          size="icon"
          aria-label={t`Dictate`}
          disabled={disabled}
          {...talk.buttonProps}
          className={cn(
            "relative size-11 shrink-0 touch-none select-none rounded-full text-foreground/75 transition-[background-color,color,transform] duration-150 motion-reduce:transition-none [-webkit-touch-callout:none]",
            // The pointer is over the button while holding, so the outline variant's hover
            // colours (a grey fill in dark, dark text) must not win over the recording red.
            recording &&
              "scale-125 border-destructive-fill bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill hover:text-destructive-foreground dark:hover:bg-destructive-fill",
          )}
        >
          <Mic size={16} strokeWidth={1.8} />
        </Button>
      </IconTooltip>
    </span>
  );
}
